// src/pages/discounts/DiscountCodesList.jsx
import { useState, useEffect, useCallback } from "react";
import {
  Percent,
  Plus,
  X,
  Trash2,
  Edit2,
  Copy,
  Check,
  Calendar,
  Users,
  Tag,
  Loader2,
  AlertCircle,
  ToggleLeft,
  ToggleRight,
  GraduationCap,
  Lock,
  Unlock,
} from "lucide-react";

// ⚠️ عدّل السطر ده عشان يطابق الـ axios instance المستخدم في باقي الصفحات
// (مثال: ProgramsList.jsx / SubscriptionsList.jsx)
import api from "../../api/axios";

/* ─── Styles ─────────────────────────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@500;700&display=swap');

  .disc-page { font-family: 'Sora', sans-serif; }

  .disc-header {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 24px; gap: 16px; flex-wrap: wrap;
  }
  .disc-header-left { display: flex; align-items: center; gap: 12px; }
  .disc-header-icon {
    width: 44px; height: 44px; border-radius: 13px;
    background: linear-gradient(135deg, rgba(79,124,255,0.14), rgba(139,92,246,0.14));
    border: 1px solid rgba(79,124,255,0.2);
    display: flex; align-items: center; justify-content: center;
  }
  .disc-title { font-size: 20px; font-weight: 700; color: var(--text-primary, #1a2035); }
  .disc-subtitle { font-size: 12.5px; color: var(--text-secondary, #6b7a99); margin-top: 2px; }

  .btn-primary {
    display: flex; align-items: center; gap: 8px;
    padding: 11px 18px; border-radius: 12px; border: none;
    background: linear-gradient(135deg, #4f7cff, #6366f1);
    color: #fff; font-size: 13.5px; font-weight: 600;
    font-family: 'Sora', sans-serif; cursor: pointer;
    box-shadow: 0 4px 16px rgba(79,124,255,0.25);
    transition: all 0.2s ease;
  }
  .btn-primary:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(79,124,255,0.32); }

  .disc-table-wrap {
    border-radius: 18px; overflow: hidden;
    background: rgba(255,255,255,0.55);
    backdrop-filter: blur(20px) saturate(180%);
    border: 1px solid rgba(255,255,255,0.88);
    box-shadow: 0 2px 20px rgba(79,124,255,0.07);
    overflow-x: auto;
  }
  .dark .disc-table-wrap {
    background: rgba(20,25,45,0.65);
    border-color: rgba(255,255,255,0.08);
  }

  .disc-table { width: 100%; border-collapse: collapse; }
  .disc-table thead th {
    text-align: right; font-size: 11px; font-weight: 700;
    letter-spacing: 0.06em; text-transform: uppercase;
    color: var(--text-secondary, #6b7a99); white-space: nowrap;
    padding: 14px 18px; border-bottom: 1px solid rgba(0,0,0,0.06);
    background: rgba(0,0,0,0.015);
  }
  .dark .disc-table thead th { border-color: rgba(255,255,255,0.06); background: rgba(255,255,255,0.02); }

  .disc-table tbody td {
    padding: 14px 18px; font-size: 13.5px;
    color: var(--text-primary, #1a2035);
    border-bottom: 1px solid rgba(0,0,0,0.04);
    white-space: nowrap;
  }
  .dark .disc-table tbody td { border-color: rgba(255,255,255,0.05); }
  .disc-table tbody tr:last-child td { border-bottom: none; }
  .disc-table tbody tr:hover { background: rgba(79,124,255,0.03); }

  .code-chip {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 6px 12px; border-radius: 8px;
    background: rgba(79,124,255,0.08); border: 1px solid rgba(79,124,255,0.18);
    font-family: 'JetBrains Mono', monospace; font-weight: 700; font-size: 13px;
    color: #4f7cff; letter-spacing: 0.03em;
  }
  .copy-btn {
    background: none; border: none; cursor: pointer; color: #4f7cff;
    display: flex; align-items: center; padding: 0; opacity: 0.7;
    transition: opacity 0.15s;
  }
  .copy-btn:hover { opacity: 1; }

  .percent-badge {
    display: inline-flex; align-items: center; gap: 4px;
    font-weight: 700; color: #059669; font-size: 13.5px;
  }

  .source-badge {
    display: inline-block; font-size: 10.5px; font-weight: 700;
    padding: 3px 9px; border-radius: 99px; margin-left: 4px;
  }
  .source-step { background: rgba(79,124,255,0.12); color: #4f7cff; }
  .source-ielts { background: rgba(249,115,22,0.12); color: #ea580c; }

  .status-badge {
    display: inline-flex; align-items: center; gap: 5px;
    font-size: 11.5px; font-weight: 600; padding: 4px 10px; border-radius: 99px;
  }
  .status-active { background: rgba(16,185,129,0.1); color: #10b981; }
  .status-inactive { background: rgba(107,114,128,0.1); color: #6b7280; }
  .status-expired { background: rgba(239,68,68,0.1); color: #ef4444; }
  .status-exhausted { background: rgba(245,158,11,0.1); color: #d97706; }

  .teacher-cell { display: flex; align-items: center; gap: 8px; }
  .teacher-avatar {
    width: 26px; height: 26px; border-radius: 8px; flex-shrink: 0;
    background: rgba(139,92,246,0.12); color: #8b5cf6;
    display: flex; align-items: center; justify-content: center;
  }
  .teacher-name-text { font-size: 13px; font-weight: 600; color: var(--text-primary, #1a2035); }
  .teacher-scope-badge {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 99px;
    margin-top: 2px;
  }
  .scope-restricted { background: rgba(239,68,68,0.1); color: #ef4444; }
  .scope-open { background: rgba(16,185,129,0.1); color: #10b981; }
  .no-teacher-text { font-size: 12.5px; color: var(--text-secondary, #6b7a99); }

  .usage-text { font-size: 12.5px; color: var(--text-secondary, #6b7a99); font-family: 'JetBrains Mono', monospace; }

  .row-actions { display: flex; align-items: center; gap: 6px; }
  .icon-btn {
    width: 32px; height: 32px; border-radius: 9px;
    display: flex; align-items: center; justify-content: center;
    border: 1px solid rgba(0,0,0,0.06); background: rgba(255,255,255,0.6);
    cursor: pointer; transition: all 0.15s;
  }
  .icon-btn:hover { background: rgba(79,124,255,0.08); border-color: rgba(79,124,255,0.2); }
  .icon-btn.danger:hover { background: rgba(239,68,68,0.08); border-color: rgba(239,68,68,0.2); color: #ef4444; }

  .empty-state {
    padding: 60px 20px; text-align: center; color: var(--text-secondary, #6b7a99);
  }
  .empty-state svg { opacity: 0.3; margin-bottom: 12px; }

  .loading-wrap {
    display: flex; align-items: center; justify-content: center;
    padding: 60px; color: var(--text-secondary, #6b7a99); gap: 10px;
  }
  .spin { animation: spin 0.8s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* ── Modal ── */
  .modal-overlay {
    position: fixed; inset: 0; z-index: 100;
    background: rgba(20,25,45,0.45); backdrop-filter: blur(6px);
    display: flex; align-items: center; justify-content: center; padding: 20px;
  }
  .modal-box {
    width: 100%; max-width: 460px; border-radius: 20px;
    background: #fff; border: 1px solid rgba(255,255,255,0.9);
    box-shadow: 0 20px 60px rgba(0,0,0,0.25);
    max-height: 90vh; overflow-y: auto;
  }
  .dark .modal-box { background: #141428; border-color: rgba(255,255,255,0.08); }

  .modal-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 20px 22px; border-bottom: 1px solid rgba(0,0,0,0.06);
  }
  .dark .modal-header { border-color: rgba(255,255,255,0.06); }
  .modal-title { font-size: 16px; font-weight: 700; color: var(--text-primary, #1a2035); }
  .modal-close-btn {
    background: none; border: none; cursor: pointer; color: var(--text-secondary, #6b7a99);
    display: flex; padding: 4px; border-radius: 8px;
  }
  .modal-close-btn:hover { background: rgba(0,0,0,0.05); }

  .modal-body { padding: 20px 22px; display: flex; flex-direction: column; gap: 16px; }

  .form-group { display: flex; flex-direction: column; gap: 6px; }
  .form-label { font-size: 12.5px; font-weight: 600; color: var(--text-primary, #1a2035); }
  .form-hint { font-size: 11.5px; color: var(--text-secondary, #6b7a99); margin-top: -2px; }
  .form-input, .form-textarea, .form-select {
    padding: 10px 13px; border-radius: 10px;
    border: 1px solid rgba(0,0,0,0.1); font-size: 13.5px;
    font-family: 'Sora', sans-serif; background: rgba(255,255,255,0.8);
    color: var(--text-primary, #1a2035); outline: none;
    transition: border-color 0.15s;
  }
  .dark .form-input, .dark .form-textarea, .dark .form-select {
    background: rgba(255,255,255,0.04); border-color: rgba(255,255,255,0.1);
    color: #e8eeff;
  }
  .form-input:focus, .form-textarea:focus, .form-select:focus { border-color: #4f7cff; }
  .form-input.mono { font-family: 'JetBrains Mono', monospace; letter-spacing: 0.05em; text-transform: uppercase; }
  .form-select { cursor: pointer; }
  .form-select:disabled { opacity: 0.55; cursor: not-allowed; }

  .form-row { display: flex; gap: 12px; }
  .form-row .form-group { flex: 1; }

  .checkbox-row {
    display: flex; align-items: center; gap: 10px;
    padding: 12px 14px; border-radius: 10px;
    border: 1px solid rgba(0,0,0,0.08); cursor: pointer;
    transition: all 0.15s;
  }
  .dark .checkbox-row { border-color: rgba(255,255,255,0.1); }
  .checkbox-row.checked { border-color: #4f7cff; background: rgba(79,124,255,0.06); }
  .checkbox-row.disabled { opacity: 0.5; cursor: not-allowed; }
  .checkbox-row input { accent-color: #4f7cff; width: 16px; height: 16px; cursor: pointer; }
  .checkbox-row-label { font-size: 13px; font-weight: 600; color: var(--text-primary, #1a2035); }
  .checkbox-row-sub { font-size: 11.5px; color: var(--text-secondary, #6b7a99); margin-top: 1px; }

  .modal-footer {
    display: flex; gap: 10px; padding: 18px 22px;
    border-top: 1px solid rgba(0,0,0,0.06);
  }
  .dark .modal-footer { border-color: rgba(255,255,255,0.06); }

  .btn-secondary {
    flex: 1; padding: 11px; border-radius: 11px;
    background: rgba(0,0,0,0.05); border: none;
    font-size: 13.5px; font-weight: 600; color: var(--text-primary, #1a2035);
    font-family: 'Sora', sans-serif; cursor: pointer;
  }
  .dark .btn-secondary { background: rgba(255,255,255,0.06); color: #e8eeff; }

  .btn-submit {
    flex: 2; padding: 11px; border-radius: 11px; border: none;
    background: linear-gradient(135deg, #4f7cff, #6366f1); color: #fff;
    font-size: 13.5px; font-weight: 700; font-family: 'Sora', sans-serif;
    cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px;
  }
  .btn-submit:disabled { opacity: 0.6; cursor: not-allowed; }

  .error-banner {
    display: flex; align-items: center; gap: 8px;
    padding: 10px 14px; border-radius: 10px;
    background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.2);
    color: #ef4444; font-size: 12.5px;
  }

  .confirm-box { padding: 22px; }
  .confirm-text { font-size: 13.5px; color: var(--text-primary, #1a2035); margin-bottom: 4px; }
  .confirm-sub { font-size: 12px; color: var(--text-secondary, #6b7a99); }
`;

const emptyForm = {
  code: "",
  percentage: "",
  applies_to_step: false,
  applies_to_ielts: false,
  max_uses: 1,
  expires_at: "",
  note: "",
  is_active: true,
  teacher: "",
  restricted_to_teacher_students: false,
};

export default function DiscountCodesList() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [teachers, setTeachers] = useState([]);
  const [teachersLoading, setTeachersLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    const id = "glass-discounts-styles";
    if (!document.getElementById(id)) {
      const tag = document.createElement("style");
      tag.id = id;
      tag.textContent = STYLES;
      document.head.appendChild(tag);
    }
  }, []);

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/discounts/admin/");
      setCodes(res.data.discount_codes || []);
    } catch (err) {
      setError("تعذر تحميل أكواد الخصم، حاول مرة أخرى");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTeachers = useCallback(async () => {
    setTeachersLoading(true);
    try {
      // ⚠️ عدّل المسار ده لو الـ endpoint بتاع المدرسين مختلف عندك
      const res = await api.get("/booking/teachers/");
      setTeachers(res.data.teachers || []);
    } catch (err) {
      // مش هنوقف الصفحة لو فشل جلب المدرسين بس هنسيب الحقل فاضي
      setTeachers([]);
    } finally {
      setTeachersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCodes();
    fetchTeachers();
  }, [fetchCodes, fetchTeachers]);

  const getTeacherName = (teacherId) => {
    if (!teacherId) return null;
    const t = teachers.find((tt) => String(tt.id) === String(teacherId));
    return t ? t.name : null;
  };

  const openCreateModal = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError("");
    setModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingId(item.id);
    setForm({
      code: item.code,
      percentage: item.percentage,
      applies_to_step: item.applies_to_step,
      applies_to_ielts: item.applies_to_ielts,
      max_uses: item.max_uses,
      expires_at: item.expires_at ? item.expires_at.slice(0, 16) : "",
      note: item.note || "",
      is_active: item.is_active,
      teacher: item.teacher ? String(item.teacher) : "",
      restricted_to_teacher_students: !!item.restricted_to_teacher_students,
    });
    setFormError("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
  };

  const handleTeacherChange = (value) => {
    setForm((prev) => ({
      ...prev,
      teacher: value,
      // لو الأدمن شال المدرس، اقفل خيار التقييد تلقائي
      restricted_to_teacher_students: value ? prev.restricted_to_teacher_students : false,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!form.code.trim()) {
      setFormError("لازم تكتب الكود");
      return;
    }
    if (!form.percentage || form.percentage < 1 || form.percentage > 100) {
      setFormError("النسبة لازم تكون رقم بين 1 و 100");
      return;
    }
    if (!form.applies_to_step && !form.applies_to_ielts) {
      setFormError("لازم تحدد الكود يشتغل مع STEP أو IELTS أو الاتنين");
      return;
    }
    if (!form.max_uses || form.max_uses < 1) {
      setFormError("عدد مرات الاستخدام لازم يكون رقم أكبر من صفر");
      return;
    }
    if (form.restricted_to_teacher_students && !form.teacher) {
      setFormError("لازم تحدد المدرس الأول عشان تقدر تقصر الكود على طلابه");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        percentage: Number(form.percentage),
        applies_to_step: form.applies_to_step,
        applies_to_ielts: form.applies_to_ielts,
        max_uses: Number(form.max_uses),
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        note: form.note,
        is_active: form.is_active,
        teacher: form.teacher ? Number(form.teacher) : null,
        restricted_to_teacher_students: form.restricted_to_teacher_students,
      };

      if (editingId) {
        await api.patch(`/discounts/admin/${editingId}/update/`, payload);
      } else {
        await api.post("/discounts/admin/create/", payload);
      }

      setModalOpen(false);
      fetchCodes();
    } catch (err) {
      const apiError =
        err?.response?.data?.code?.[0] ||
        err?.response?.data?.error ||
        err?.response?.data?.non_field_errors?.[0] ||
        err?.response?.data?.teacher?.[0] ||
        "حدث خطأ، حاول مرة أخرى";
      setFormError(apiError);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item) => {
    try {
      await api.patch(`/discounts/admin/${item.id}/update/`, {
        is_active: !item.is_active,
      });
      setCodes((prev) =>
        prev.map((c) => (c.id === item.id ? { ...c, is_active: !c.is_active } : c))
      );
    } catch {
      setError("تعذر تحديث حالة الكود");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/discounts/admin/${deleteTarget.id}/delete/`);
      setCodes((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch {
      setError("تعذر حذف الكود");
    } finally {
      setDeleting(false);
    }
  };

  const copyCode = (item) => {
    navigator.clipboard.writeText(item.code);
    setCopiedId(item.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const getStatus = (item) => {
    if (!item.is_active) return { label: "غير نشط", cls: "status-inactive" };
    if (item.expires_at && new Date(item.expires_at) < new Date())
      return { label: "منتهي", cls: "status-expired" };
    if (item.times_used >= item.max_uses)
      return { label: "مستنفد", cls: "status-exhausted" };
    return { label: "نشط", cls: "status-active" };
  };

  return (
    <div className="disc-page">
      <div className="disc-header">
        <div className="disc-header-left">
          <div className="disc-header-icon">
            <Percent size={20} color="#4f7cff" />
          </div>
          <div>
            <div className="disc-title">Discount Codes</div>
            <div className="disc-subtitle">
              إدارة أكواد الخصم لـ STEP و IELTS
            </div>
          </div>
        </div>
        <button className="btn-primary" onClick={openCreateModal}>
          <Plus size={16} />
          إنشاء كود جديد
        </button>
      </div>

      {error && (
        <div className="error-banner" style={{ marginBottom: 16 }}>
          <AlertCircle size={15} />
          {error}
        </div>
      )}

      <div className="disc-table-wrap">
        {loading ? (
          <div className="loading-wrap">
            <Loader2 size={20} className="spin" />
            جاري التحميل...
          </div>
        ) : codes.length === 0 ? (
          <div className="empty-state">
            <Tag size={38} />
            <div>مفيش أكواد خصم لسه، اعمل أول كود</div>
          </div>
        ) : (
          <table className="disc-table">
            <thead>
              <tr>
                <th>الكود</th>
                <th>المدرس</th>
                <th>النسبة</th>
                <th>يعمل مع</th>
                <th>الاستخدام</th>
                <th>الحالة</th>
                <th>ينتهي في</th>
                <th style={{ textAlign: "center" }}>إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((item) => {
                const status = getStatus(item);
                const teacherName = item.teacher_name || getTeacherName(item.teacher);
                return (
                  <tr key={item.id}>
                    <td>
                      <div className="code-chip">
                        <Tag size={13} />
                        {item.code}
                        <button
                          className="copy-btn"
                          onClick={() => copyCode(item)}
                          title="نسخ الكود"
                        >
                          {copiedId === item.id ? (
                            <Check size={13} color="#10b981" />
                          ) : (
                            <Copy size={13} />
                          )}
                        </button>
                      </div>
                    </td>
                    <td>
                      {teacherName ? (
                        <div className="teacher-cell">
                          <div className="teacher-avatar">
                            <GraduationCap size={14} />
                          </div>
                          <div>
                            <div className="teacher-name-text">{teacherName}</div>
                            <span
                              className={`teacher-scope-badge ${
                                item.restricted_to_teacher_students
                                  ? "scope-restricted"
                                  : "scope-open"
                              }`}
                            >
                              {item.restricted_to_teacher_students ? (
                                <>
                                  <Lock size={9} />
                                  طلابه بس
                                </>
                              ) : (
                                <>
                                  <Unlock size={9} />
                                  مفتوح للكل
                                </>
                              )}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <span className="no-teacher-text">— بدون مدرس —</span>
                      )}
                    </td>
                    <td>
                      <span className="percent-badge">
                        <Percent size={13} />
                        {item.percentage}%
                      </span>
                    </td>
                    <td>
                      {item.applies_to_step && (
                        <span className="source-badge source-step">STEP</span>
                      )}
                      {item.applies_to_ielts && (
                        <span className="source-badge source-ielts">IELTS</span>
                      )}
                    </td>
                    <td>
                      <span className="usage-text">
                        <Users size={12} style={{ display: "inline", marginLeft: 4 }} />
                        {item.times_used} / {item.max_uses}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${status.cls}`}>
                        {status.label}
                      </span>
                    </td>
                    <td>
                      <span className="usage-text">
                        {item.expires_at ? (
                          <>
                            <Calendar size={12} style={{ display: "inline", marginLeft: 4 }} />
                            {new Date(item.expires_at).toLocaleDateString("ar-EG")}
                          </>
                        ) : (
                          "بدون تاريخ"
                        )}
                      </span>
                    </td>
                    <td>
                      <div className="row-actions">
                        <button
                          className="icon-btn"
                          onClick={() => toggleActive(item)}
                          title={item.is_active ? "تعطيل الكود" : "تفعيل الكود"}
                        >
                          {item.is_active ? (
                            <ToggleRight size={16} color="#10b981" />
                          ) : (
                            <ToggleLeft size={16} color="#9ca3af" />
                          )}
                        </button>
                        <button
                          className="icon-btn"
                          onClick={() => openEditModal(item)}
                          title="تعديل"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          className="icon-btn danger"
                          onClick={() => setDeleteTarget(item)}
                          title="حذف"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Create / Edit Modal ── */}
      {modalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                {editingId ? "تعديل كود الخصم" : "إنشاء كود خصم جديد"}
              </div>
              <button className="modal-close-btn" onClick={closeModal}>
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {formError && (
                  <div className="error-banner">
                    <AlertCircle size={15} />
                    {formError}
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">الكود</label>
                  <input
                    type="text"
                    className="form-input mono"
                    placeholder="SUMMER50"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">نسبة الخصم %</label>
                    <input
                      type="number"
                      min={1}
                      max={100}
                      className="form-input"
                      placeholder="20"
                      value={form.percentage}
                      onChange={(e) => setForm({ ...form, percentage: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">عدد مرات الاستخدام</label>
                    <input
                      type="number"
                      min={1}
                      className="form-input"
                      placeholder="1"
                      value={form.max_uses}
                      onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">يعمل مع</label>
                  <div className="form-row">
                    <label
                      className={`checkbox-row ${form.applies_to_step ? "checked" : ""}`}
                      style={{ flex: 1 }}
                    >
                      <input
                        type="checkbox"
                        checked={form.applies_to_step}
                        onChange={(e) =>
                          setForm({ ...form, applies_to_step: e.target.checked })
                        }
                      />
                      <span className="checkbox-row-label">STEP</span>
                    </label>
                    <label
                      className={`checkbox-row ${form.applies_to_ielts ? "checked" : ""}`}
                      style={{ flex: 1 }}
                    >
                      <input
                        type="checkbox"
                        checked={form.applies_to_ielts}
                        onChange={(e) =>
                          setForm({ ...form, applies_to_ielts: e.target.checked })
                        }
                      />
                      <span className="checkbox-row-label">IELTS</span>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">المدرس المسؤول (اختياري)</label>
                  <select
                    className="form-select"
                    value={form.teacher}
                    onChange={(e) => handleTeacherChange(e.target.value)}
                    disabled={teachersLoading}
                  >
                    <option value="">— بدون مدرس (كود عام) —</option>
                    {teachers.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  <div className="form-hint">
                    {teachersLoading
                      ? "جاري تحميل قائمة المدرسين..."
                      : "المدرس المرتبط بالكود، هيظهر في تقاريره ويقدر يوزعه على طلابه"}
                  </div>
                </div>

                <label
                  className={`checkbox-row ${
                    form.restricted_to_teacher_students ? "checked" : ""
                  } ${!form.teacher ? "disabled" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={form.restricted_to_teacher_students}
                    disabled={!form.teacher}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        restricted_to_teacher_students: e.target.checked,
                      })
                    }
                  />
                  <div>
                    <div className="checkbox-row-label">
                      اقتصار الكود على طلاب المدرس فقط
                    </div>
                    <div className="checkbox-row-sub">
                      {form.teacher
                        ? "لو مفعّل، الكود مش هيشتغل إلا لطالب مشترك (paid) في برنامج لنفس المدرس"
                        : "لازم تحدد مدرس الأول عشان تقدر تفعّل الخيار ده"}
                    </div>
                  </div>
                </label>

                <div className="form-group">
                  <label className="form-label">تاريخ الانتهاء (اختياري)</label>
                  <input
                    type="datetime-local"
                    className="form-input"
                    value={form.expires_at}
                    onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">ملاحظة (اختياري، للأدمن بس)</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="مثال: كود حملة رمضان"
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                  />
                </div>

                {editingId && (
                  <label
                    className={`checkbox-row ${form.is_active ? "checked" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) =>
                        setForm({ ...form, is_active: e.target.checked })
                      }
                    />
                    <span className="checkbox-row-label">الكود نشط</span>
                  </label>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={saving}
                >
                  إلغاء
                </button>
                <button type="submit" className="btn-submit" disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 size={15} className="spin" />
                      جاري الحفظ...
                    </>
                  ) : editingId ? (
                    "حفظ التعديلات"
                  ) : (
                    "إنشاء الكود"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation ── */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
          <div
            className="modal-box"
            style={{ maxWidth: 380 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="confirm-box">
              <div className="confirm-text">
                متأكد إنك عايز تحذف الكود{" "}
                <strong style={{ fontFamily: "JetBrains Mono, monospace" }}>
                  {deleteTarget.code}
                </strong>
                ؟
              </div>
              <div className="confirm-sub">
                الإجراء ده مش هينفع يتراجع فيه، لكن سجل الاستخدامات السابقة (لو موجود) هيفضل محفوظ.
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                إلغاء
              </button>
              <button
                className="btn-submit"
                style={{ background: "linear-gradient(135deg, #ef4444, #dc2626)" }}
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <Loader2 size={15} className="spin" />
                    جاري الحذف...
                  </>
                ) : (
                  "حذف نهائي"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}